import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatDialog, MatDialogModule } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTableModule } from '@angular/material/table'
import { ActivatedRoute, Router } from '@angular/router'
import { HeaderBarComponent } from '@components/header-bar/header-bar.component'
import { DialogLayerIntegrationComponent } from '@components/integration-platform/dialog-layer-integration/dialog-layer-integration.component'
import { IntegrationSidebarComponent } from '@components/integration-platform/integration-sidebar/integration-sidebar.component'
import { ADMIN_GEOM_TABLE_NAMES } from '@core/constants/constant.data'
import { LayerCloned, LayerIntegration } from '@core/models/layers.model'
import { LayersClonedService } from '@core/services/layers-cloned.service'

const ALL_STEP_COLUMNS: string[] = [
  'integrationId',
  'integrationDate',
  'integrationRepoName',
  'integrationHashCommit',
  'intoDb',
  'statsPrecompute',
  'geoserverTiff',
  'geoserverPyramidTiff',
  'deploymentGeoserver',
]

// Steps a given layer type never runs, per the backend integration handlers: these columns can never be
// anything but "Not processed" for that type, so showing them just reads as an incomplete/failed integration.
const HIDDEN_COLUMNS_BY_LAYER_TYPE: Record<string, string[]> = {
  Raster: ['geoserverTiff', 'deploymentGeoserver'],
  Vector: ['statsPrecompute', 'geoserverTiff', 'geoserverPyramidTiff'],
  Tabular: ['statsPrecompute', 'geoserverTiff', 'geoserverPyramidTiff'],
}

@Component({
  selector: 'app-integration-layer-detailed',
  templateUrl: './integration-layer-detailed.component.html',
  styleUrls: ['./integration-layer-detailed.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardModule,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    HeaderBarComponent,
    IntegrationSidebarComponent,
  ],
})
export class IntegrationLayersDetailedComponent {
  layersClonedService = inject(LayersClonedService)
  private activatedRoute = inject(ActivatedRoute)
  private router = inject(Router)
  layersCloned$: Signal<LayerCloned[]> = this.layersClonedService.layersCloned$
  readonly dialog = inject(MatDialog)
  selectedLayerCloned$ = signal<LayerCloned | null>(null)
  layerIntegration$ = signal<LayerIntegration[]>([])
  displayedColumns = computed(() => {
    const hiddenColumns = HIDDEN_COLUMNS_BY_LAYER_TYPE[this.selectedLayerCloned$()?.layerType ?? '']
    return hiddenColumns
      ? ALL_STEP_COLUMNS.filter((column) => !hiddenColumns.includes(column))
      : ALL_STEP_COLUMNS
  })
  private _routeSub = this.activatedRoute.paramMap.subscribe()
  isLoadingHistory$ = signal<boolean>(false)

  constructor() {}

  ngOnInit(): void {
    this._routeSub = this.activatedRoute.paramMap.subscribe(async (params) => {
      const layerName = params.get('layerName')

      if (!layerName) {
        this.layerIntegration$.set([])
        return
      }

      await this.loadLayerHistory(layerName)
    })
  }

  ngOnDestroy(): void {
    this._routeSub.unsubscribe()
  }

  async refreshLayerIntegrationHistory() {
    const layerName = this.activatedRoute.snapshot.paramMap.get('layerName')
    if (!layerName) {
      this.layerIntegration$.set([])
      return
    }
    await this.loadLayerHistory(layerName)
  }

  backToLayersCloned() {
    this.router.navigate(['/integration/layers'])
  }

  completeLayerIntegration(repoName: string, layerType: string) {
    const integrationDialogRef = this.dialog.open(DialogLayerIntegrationComponent, {
      data: {
        repoName: repoName,
        layerType: layerType,
        adminGeomTableNames: ADMIN_GEOM_TABLE_NAMES,
      },
    })
    integrationDialogRef.afterClosed().subscribe((result) => this._refreshAfterAction(result))
  }

  private _refreshAfterAction(result: boolean | undefined) {
    if (result) {
      this.refreshLayerIntegrationHistory()
    }
  }

  private async loadLayerHistory(layerName: string) {
    if (this.isLoadingHistory$()) {
      return
    }

    this.isLoadingHistory$.set(true)
    try {
      await this.layersClonedService.refreshLayersCloned()
      const layerFromCloned = this.layersCloned$().find((layer) => layer.layerName === layerName)
      const layerIntegrations = await this.layersClonedService.getLayerIntegration(layerName)
      this.selectedLayerCloned$.set({
        layerName,
        layerType: layerFromCloned?.layerType || '',
        repo: layerFromCloned?.repo || '',
        repoUrl: layerFromCloned?.repoUrl || '',
        errors: layerFromCloned?.errors,
      })

      this.layerIntegration$.set(layerIntegrations)
    } finally {
      this.isLoadingHistory$.set(false)
    }
  }

  formatExecutionDate(date?: string): string {
    if (!date) {
      return '-'
    }
    const [datePart, timePart] = date.split('T')
    if (!datePart || !timePart) {
      return date
    }

    const [year, month, day] = datePart.split('-')
    if (!year || !month || !day) {
      return date
    }
    const time = timePart.split('.')[0]
    return `${day}/${month}/${year} ${time}Z`
  }

  trackBy(_index: number, item: LayerIntegration): string {
    return `${item.id}`
  }

  getUrlHashCommit(hashCommit: string): string | null {
    const repoUrl = this.selectedLayerCloned$()?.repoUrl || ''

    if (!hashCommit || !repoUrl) {
      return null
    }

    return repoUrl.replace(/\.git$/, '') + '/commit/' + hashCommit
  }
}
